import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import icons from "../../../assets/images/constantIcons";
import AddCustomerForm from "../Customer/AddCustomerForm";
import Select from "react-select";
import apiService, { handleError } from "../../Common/ApiCommon/ApiService";
import toast from "react-hot-toast";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { TimePicker } from "antd";
import dayjs from "dayjs";

// To convert time into 12 hour format
const converttime12hrFormat = (timeRange) => {
  if (!timeRange) return "";

  const times = timeRange.split(" - ");
  if (times.length !== 2) return timeRange;

  const convertTo12Hr = (time) => {
    const [hours, minutes] = time.split(":").map(Number);
    const ampm = hours >= 12 ? "PM" : "AM";
    const hour = hours % 12 || 12; // Convert 0 to 12
    return `${hour}:${minutes.toString().padStart(2, "0")} ${ampm}`;
  };

  return `${convertTo12Hr(times[0])} - ${convertTo12Hr(times[1])}`;
};

const AddPlayerForm = ({ isOpen, onClose, fetchPlayers }) => {
  const [customerData, setCustomerData] = useState([]);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [teamAPlayers, setTeamAPlayers] = useState([]);
  const [teamBPlayers, setTeamBPlayers] = useState([]);

  // fetch customers players list
  const fetchCustomers = async () => {
    try {
      const response = await apiService.get("/customer_users?is_active=true");
      setCustomerData(response.data);
    } catch (errors) {
      // setError(errors);
      handleError(errors);
    }
  };
  // fetch table data
  const fetchTabledata = async () => {
    const response = await apiService.get(`/tables?active=true`);
    setTableData(response?.data);
  };

  useEffect(() => {
    fetchCustomers();
    fetchTabledata();
  }, []);

  const playerOptions = customerData?.map((customer) => ({
    label: customer.full_name,
    value: customer.id,
  }));

  // get current time in default
  const getCurrentTime = () => {
    const now = new Date();
    const minutes = now.getMinutes();
    const roundedMinutes = Math.ceil(minutes / 5) * 5;

    if (roundedMinutes === 60) {
      now.setHours(now.getHours() + 1);
      now.setMinutes(0);
    } else {
      now.setMinutes(roundedMinutes);
    }

    let hours = now.getHours();
    const mins = now.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    return `${String(hours).padStart(2, "0")}:${mins} ${ampm}`;
  };

  useEffect(() => {
    if (isOpen) {
      formik.setFieldValue("start_time", getCurrentTime());
    }
  }, [isOpen]);

  useEffect(() => {
    formik.setFieldValue("players", [...teamAPlayers, ...teamBPlayers], false);
  }, [teamAPlayers, teamBPlayers]);

  const formik = useFormik({
    initialValues: {
      date: new Date().toISOString().split("T")[0],
      players: [],
      start_time: getCurrentTime(),
      table_id: "",
    },
    validationSchema: Yup.object({
      teamAPlayers: Yup.array().of(Yup.number()),
      teamBPlayers: Yup.array().of(Yup.number()),
      players: Yup.mixed().test(
        "at-least-one-player",
        "At least one player must be selected",
        function () {
          const { teamAPlayers, teamBPlayers } = this.parent;
          return (teamAPlayers?.length || 0) + (teamBPlayers?.length || 0) > 0;
        }
      ),
      date: Yup.string().required("Date is required"),
      start_time: Yup.string().required("Start Time is required"),
      table_id: Yup.string().required("Please select a table"),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        let formattedStartTime = values.start_time;

        // Check if the value is already in 12-hour format using regex
        const is12HrFormat = /^(0?[1-9]|1[0-2]):[0-5][0-9] (AM|PM)$/i.test(
          values.start_time
        );

        if (!is12HrFormat) {
          // Convert from 24hr to 12hr format
          formattedStartTime = apiService.convert24To12Hr(values.start_time);
        }

        const payload = {
          booking: {
            table_id: values.table_id,
            start_time: formattedStartTime,
            date: values.date,
            players: formik.values.players,
          },
        };

        const response = await apiService.post("/bookings", payload);

        // Check if the request was successful
        if (response.status === 200) {
          onClose();
          toast.success(response?.message, { duration: 2000 });
          formik.resetForm();
          setShowDetails(false);
          setSelectedTable(null); // reset selected table
          setTeamAPlayers([]);
          setTeamBPlayers([]);
          fetchPlayers();
        }
      } catch (errors) {
        handleError(errors);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleTableChange = (e) => {
    const tableId = e.target.value;
    formik.setFieldValue("table_id", tableId);
    const tableDetails = tableData.find(
      (table) => table.id.toString() === tableId
    );
    setSelectedTable(tableDetails || null);
  };

  const handleClose = () => {
    setShowDetails(false);
    setSelectedTable(null); // reset selected table
    setTeamAPlayers([]);
    setTeamBPlayers([]);
    formik.resetForm(); // reset form values
    onClose(); // close the form
  };

  return (
    isOpen && (
      <>
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg p-4 s:p-6 w-[310px] s:w-[350px] md:w-[460px]  relative">
            <div
              className="absolute top-7 right-3 bg-green50 rounded-full p-2 cursor-pointer w-[32px] h-[32px]"
              onClick={handleClose}
            >
              <icons.IoClose className=" text-green700 " />
            </div>
            <h2 className="text-xl font-semibold mb-5 text-G500">
              Add Booking
            </h2>
            <form
              onSubmit={formik.handleSubmit}
              className="space-y-4 h-[70vh] overflow-y-auto vertical"
            >
              {/* Table Dropdown */}
              <div className="text-G300">
                <div className="flex justify-between">
                  {" "}
                  <label className="block text-sm font-normal mb-2">
                    Table <span className="text-red ">*</span>
                  </label>{" "}
                </div>
                {tableData && tableData.length > 0 ? (
                  <select
                    name="table_id"
                    value={formik.values.table_id}
                    onChange={handleTableChange}
                    onBlur={formik.handleBlur}
                    className="inputtext placeholder:text-G50"
                  >
                    <option value="">Select a table</option>
                    {tableData?.map((tabledata) => (
                      <option key={tabledata.id} value={tabledata.id}>
                        {tabledata.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm">No tables available</p>
                )}

                {selectedTable && (
                  <button
                    type="button"
                    className="text-sm text-green hover:text-blue hover:underline mt-1"
                    onMouseEnter={() => setShowDetails(true)}
                    onMouseLeave={() => setShowDetails(false)}
                  >
                    View table details
                  </button>
                )}
                {showDetails && selectedTable && (
                  <div
                    className="absolute bg-gray-50 p-2 shadow-md border rounded-md w-auto z-30"
                    onMouseEnter={() => setShowDetails(true)}
                    onMouseLeave={() => setShowDetails(false)}
                  >
                    <table className="w-full border-collapse border border-gray-300">
                      <tbody>
                        {[
                          "Day Charge",
                          "Night Charge",
                          "Day Time Range",
                          "Night Time Range",
                        ].map((label, index) => (
                          <tr key={index}>
                            <td className="border px-4 py-1 font-medium text-sm">
                              {label}
                            </td>
                            <td className="border px-4 py-1 text-sm">
                              {label.includes("Charge")
                                ? `₹${
                                    label.includes("Day")
                                      ? selectedTable.day_charge
                                      : selectedTable.night_charge
                                  }`
                                : converttime12hrFormat(
                                    label.includes("Day")
                                      ? selectedTable.day_time_range
                                      : selectedTable.night_time_range
                                  )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {formik.errors.table_id && formik.touched.table_id && (
                  <div className="text-red text-sm">
                    {formik.errors.table_id}
                  </div>
                )}
              </div>

              {/* Select Players using react-select */}
              <div className="text-G300">
                <div className="flex justify-between items-start mb-2">
                  <label className="block text-sm font-medium">Players <span className="text-red ">*</span></label>
                  <button
                    type="button"
                    onClick={() => setIsCustomerModalOpen(true)}
                    className="text-sm text-green border border-green px-2 py-1 rounded-md hover:bg-green hover:text-white"
                  >
                    Add Players
                  </button>
                </div>

                <div className="flex flex-col gap-1 items-center">
                  {/* Team A Select */}
                  <div className="w-full">
                    <div className="text-sm  mb-1">Team A</div>
                    <Select
                      isMulti
                      options={playerOptions?.filter(
                        (option) => !teamBPlayers?.includes(option.value)
                      )}
                      value={playerOptions?.filter((opt) =>
                        teamAPlayers?.includes(opt.value)
                      )}
                      onChange={(selected) => {
                        const values = selected.map((s) => s.value);
                        setTeamAPlayers(values);
                        formik.setFieldValue("teamAPlayers", values);
                        formik.validateField("players");
                      }}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          boxShadow: "none",
                          minHeight: "38px",
                        }),
                        placeholder: (provided) => ({
                          ...provided,
                          fontSize: "14px",
                        }),
                      }}
                      classNamePrefix="select "
                      placeholder="Select players..."
                    />
                  </div>

                  {/* VS */}
                  <p className=" text-center font-semibold text-gray-500 text-base ">
                    {" "}
                    vs{" "}
                  </p>

                  {/* Team B Select */}
                  <div className="w-full">
                    <div className="text-sm mb-1">Team B</div>
                    <Select
                      isMulti
                      options={playerOptions?.filter(
                        (option) => !teamAPlayers?.includes(option.value)
                      )}
                      value={playerOptions?.filter((opt) =>
                        teamBPlayers?.includes(opt.value)
                      )}
                      onChange={(selected) => {
                        const values = selected.map((s) => s.value);
                        setTeamBPlayers(values);
                        formik.setFieldValue("teamBPlayers", values);
                        formik.validateField("players");
                      }}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          boxShadow: "none",
                          minHeight: "38px",
                        }),
                        placeholder: (provided) => ({
                          ...provided,
                          fontSize: "14px",
                        }),
                      }}
                      classNamePrefix="select"
                      placeholder="Select players..."
                    />
                  </div>
                </div>
                {formik.errors.players && formik.touched.players && (
                  <div className="text-red text-sm mt-1">
                    {formik.errors.players}
                  </div>
                )}
              </div>

              <div className="text-G300 w-full">
                <label className="block text-sm font-normal mb-2">Date <span className="text-red ">*</span></label>
                <div className="relative">
                  <DatePicker
                    selected={
                      formik.values.date ? new Date(formik.values.date) : null
                    }
                    onChange={(date) =>
                      formik.setFieldValue(
                        "date",
                        date.toISOString().split("T")[0]
                      )
                    }
                    minDate={(() => {
                      const yesterday = new Date();
                      yesterday.setDate(yesterday.getDate() - 1);
                      yesterday.setHours(0, 0, 0, 0);
                      return yesterday;
                    })()}
                    maxDate={(() => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      return today;
                    })()}
                    dateFormat="yyyy-MM-dd"
                    className="inputtext placeholder:text-G50 w-full pr-10"
                  />
                  <icons.MdOutlineCalendarMonth className="absolute right-3 top-3 text-green pointer-events-none" />
                </div>
                {formik.errors.date && formik.touched.date && (
                  <div className="text-red text-sm">{formik.errors.date}</div>
                )}
              </div>

              <div className="text-G300 flex flex-col">
                <label className="block text-sm font-normal mb-2">
                  Start Time <span className="text-red ">*</span>
                </label>
                <div className="w-full custom-timepicker">
                  <TimePicker
                    value={
                      formik.values.start_time
                        ? dayjs(formik.values.start_time, "hh:mm A")
                        : null
                    }
                    format="hh:mm A"
                    use12Hours
                    minuteStep={5}
                    allowClear={false}
                    inputReadOnly={true}
                    showNow={true}
                    className="inputtext w-full focus:outline-none outline-none"
                    onSelect={(time) => {
                      if (time) {
                        formik.setFieldValue(
                          "start_time",
                          time.format("hh:mm A")
                        );
                      }
                    }}
                    popupClassName="custom-timepicker-popup"
                    getPopupContainer={() => document.body}
                  />
                </div>
                {formik.touched.start_time && formik.errors.start_time && (
                  <div className="text-red text-sm">
                    {formik.errors.start_time}
                  </div>
                )}
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="w-[75px] sm:w-[93px] text-sm py-2 text-center border border-green text-green rounded-lg"
                >
                  Close
                </button>
                <button
                  type="submit"
                  className={`w-[75px] sm:w-[93px] text-sm text-center py-2 rounded-lg ${
                    isSubmitting
                      ? "bg-green50 cursor-not-allowed"
                      : "bg-green text-white"
                  }`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
          {/* Add Customer Modal */}
          {isCustomerModalOpen && (
            <AddCustomerForm
              isOpen={isCustomerModalOpen}
              onClose={() => setIsCustomerModalOpen(false)}
              fetchCustomers={fetchCustomers}
            />
          )}
        </div>
      </>
    )
  );
};

export default AddPlayerForm;
